<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ArrowRight, ChevronDown, ExternalLink, Languages, Menu, X } from '@lucide/vue'
import AppButton from '@/components/ui/AppButton.vue'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()

const isScrolled = ref(false)
const openMenu = ref<'products' | 'company' | 'resources' | null>(null)
const langOpen = ref(false)
const mobileOpen = ref(false)
const mobileExpanded = ref<'products' | 'company' | 'resources' | null>(null)

function handleScroll() {
  isScrolled.value = window.scrollY > 10
}

onMounted(() => window.addEventListener('scroll', handleScroll, { passive: true }))
onUnmounted(() => window.removeEventListener('scroll', handleScroll))

function closeMenus() {
  openMenu.value = null
  langOpen.value = false
}

function toggleMobile(name: 'products' | 'company' | 'resources') {
  mobileExpanded.value = mobileExpanded.value === name ? null : name
}

function closeMobile() {
  mobileOpen.value = false
  mobileExpanded.value = null
}

watch(() => route.params.locale, (val) => {
  if (val === 'en' || val === 'ja') locale.value = val
}, { immediate: true })

function selectLocale(next: 'en' | 'ja') {
  langOpen.value = false
  if (next === locale.value) return
  const newPath = route.path.replace(`/${locale.value}`, `/${next}`)
  locale.value = next
  router.push({ path: newPath, query: route.query })
}

function isActiveLocale(val: string) {
  return locale.value === val
}

function navPath(path: string) {
  return `/${locale.value}${path}`
}

const relativePath = computed(() => route.path.replace(/^\/(en|ja)/, ''))
const isProductsActive = computed(() => relativePath.value.startsWith('/products'))
const isCompanyActive = computed(() => relativePath.value.startsWith('/company'))
const isResourcesActive = computed(() => relativePath.value.startsWith('/blog'))

const productMenu = computed(() => [
  { name: 'Personalizer', desc: t('header.personalizerDesc'), path: '/products/personalizer', successPath: '/products/personalizer/success-stories' },
  { name: 'Maira', desc: t('header.mairaDesc'), path: '/products/maira', successPath: '/products/maira/success-stories' },
  { name: 'G-Core', desc: t('header.gcoreDesc'), path: '/products/gcore', successPath: null as string | null },
  { name: 'SmartAds', desc: t('header.smartadsDesc'), path: '/products/smartads', successPath: '/products/smartads/success-stories' },
])

const companyMenu = computed(() => [
  { label: t('footer.aboutUs'), desc: t('header.aboutUsDesc'), path: '/company/about-us' },
  { label: t('nav.corporateProfile'), desc: t('header.corporateProfileDesc'), path: '/company/corporate-profile' },
  { label: t('nav.ourTeam'), desc: t('header.ourTeamDesc'), path: '/company/team' },
  { label: t('nav.career'), desc: t('header.careerDesc'), path: '/company/career' },
  { label: t('footer.lifeAtGigalogy'), desc: t('header.lifeAtGigalogyDesc'), path: '/blog?tag=insights' },
])

const resourceMenu = computed(() => [
  { label: t('header.blogs'), desc: t('header.blogDesc'), path: '/blog', external: false, href: '' },
  { label: t('header.documentation'), desc: t('header.documentationDesc'), path: '', external: true, href: 'https://api.recommender.gigalogy.com/v1/documentation' },
  { label: t('header.tutorial'), desc: t('header.tutorialDesc'), path: '', external: true, href: 'https://tutorial.gigalogy.com/' },
  { label: t('nav.events'), desc: t('header.eventsDesc'), path: '', external: true, href: 'https://event.gigalogy.com' },
])
</script>

<template>
  <!-- Desktop header -->
  <header
    class="fixed top-0 left-0 right-0 z-50 border-b transition-all duration-200"
    :class="isScrolled
      ? 'bg-white shadow-header border-border'
      : 'bg-transparent border-border'"
    @mouseleave="closeMenus"
  >
    <div class="page-container flex h-20 items-center justify-between">

      <!-- Logo -->
      <RouterLink :to="navPath('')" class="shrink-0 flex items-center gap-2" @click="closeMobile">
        <img src="/images/gigalogy-logo.png" alt="Gigalogy" class="h-9 w-auto" />
      </RouterLink>

      <!-- Desktop nav -->
      <nav class="hidden lg:flex h-full items-center gap-10">

        <!-- Home -->
        <RouterLink
          :to="navPath('')"
          class="text-body-lg text-black-dark hover:text-primary transition-colors"
          exact-active-class="text-primary"
        >
          {{ t('nav.home') }}
        </RouterLink>

        <!-- Products -->
        <div class="h-full flex items-center" @mouseenter="openMenu = 'products'">
          <button
            class="flex items-center gap-1 text-body-lg text-black-dark hover:text-primary transition-colors cursor-pointer"
            :class="openMenu === 'products' || isProductsActive ? 'text-primary' : ''"
          >
            {{ t('nav.products') }}
            <ChevronDown :size="14" class="transition-transform duration-200" :class="openMenu === 'products' ? 'rotate-180' : ''" />
          </button>

          <!-- Products submenu -->
          <div
            v-if="openMenu === 'products'"
            class="absolute top-full left-1/2 -translate-x-1/2 mt-0 pt-0.5 w-max z-50"
          >
            <div class="bg-white rounded-md shadow-dropdown flex p-4 gap-0">
              <div
                v-for="(item, i) in productMenu"
                :key="item.path"
                class="flex flex-col gap-3 py-2 px-5 w-[270px]"
                :class="i < productMenu.length - 1 ? 'border-r border-border' : ''"
              >
                <div>
                  <p class="font-semibold text-primary-dark text-h6">{{ item.name }}</p>
                  <p class="text-black-dark text-caption-sm mt-1">{{ item.desc }}</p>
                </div>
                <hr class="border-border/50" />
                <div class="flex flex-col gap-3">
                  <RouterLink :to="navPath(item.path)" class="group flex items-center gap-1 text-primary text-caption-md hover:underline" @click="closeMenus">
                    {{ t('header.explorePlatform') }} <ArrowRight :size="13" class="transition-transform duration-200 group-hover:translate-x-1" />
                  </RouterLink>
                  <RouterLink v-if="item.successPath" :to="navPath(item.successPath)" class="group flex items-center gap-1 text-primary text-caption-md hover:underline" @click="closeMenus">
                    {{ t('header.exploreSuccessStories') }} <ArrowRight :size="13" class="transition-transform duration-200 group-hover:translate-x-1" />
                  </RouterLink>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Company -->
        <div class="relative h-full flex items-center" @mouseenter="openMenu = 'company'">
          <button
            class="flex items-center gap-1 text-body-lg text-black-dark hover:text-primary transition-colors cursor-pointer"
            :class="openMenu === 'company' || isCompanyActive ? 'text-primary' : ''"
          >
            {{ t('nav.company') }}
            <ChevronDown :size="14" class="transition-transform duration-200" :class="openMenu === 'company' ? 'rotate-180' : ''" />
          </button>

          <!-- Company submenu -->
          <div
            v-if="openMenu === 'company'"
            class="absolute top-full left-1/2 -translate-x-1/2 mt-0 pt-0.5 z-50"
          >
            <div class="bg-white rounded-md shadow-dropdown flex flex-col gap-1 p-2 w-[314px]">
              <RouterLink
                v-for="item in companyMenu"
                :key="item.path"
                :to="navPath(item.path)"
                class="flex items-center justify-between rounded-md px-4 py-3.5 hover:bg-bg-light transition-colors group"
                @click="closeMenus"
              >
                <div>
                  <p class="font-semibold text-primary-dark text-btn-lg">{{ item.label }}</p>
                  <p class="text-black-dark text-caption-sm mt-0.5">{{ item.desc }}</p>
                </div>
                <ArrowRight :size="16" class="text-black-dark shrink-0 ml-2 transition-transform duration-200 group-hover:translate-x-1" />
              </RouterLink>
            </div>
          </div>
        </div>

        <!-- Resources -->
        <div class="relative h-full flex items-center" @mouseenter="openMenu = 'resources'">
          <button
            class="flex items-center gap-1 text-body-lg text-black-dark hover:text-primary transition-colors cursor-pointer"
            :class="openMenu === 'resources' || isResourcesActive ? 'text-primary' : ''"
          >
            {{ t('nav.resources') }}
            <ChevronDown :size="14" class="transition-transform duration-200" :class="openMenu === 'resources' ? 'rotate-180' : ''" />
          </button>

          <!-- Resources submenu -->
          <div
            v-if="openMenu === 'resources'"
            class="absolute top-full left-1/2 -translate-x-1/2 mt-0 pt-0.5 z-50"
          >
            <div class="bg-white rounded-md shadow-dropdown flex flex-col gap-1 p-2 w-[314px]">
              <component
                :is="item.external ? 'a' : 'RouterLink'"
                v-for="item in resourceMenu"
                :key="item.label"
                v-bind="item.external
                  ? { href: item.href, target: '_blank', rel: 'noopener noreferrer' }
                  : { to: navPath(item.path) }"
                class="flex items-center justify-between rounded-md px-4 py-3.5 hover:bg-bg-light transition-colors group"
                @click="closeMenus"
              >
                <div>
                  <p class="font-semibold text-primary-dark text-btn-lg">{{ item.label }}</p>
                  <p class="text-black-dark text-caption-sm mt-0.5">{{ item.desc }}</p>
                </div>
                <ExternalLink v-if="item.external" :size="16" class="text-black-dark shrink-0 ml-2" />
                <ArrowRight v-else :size="16" class="text-black-dark shrink-0 ml-2 transition-transform duration-200 group-hover:translate-x-1" />
              </component>
            </div>
          </div>
        </div>

        <!-- Contact Us -->
        <RouterLink
          :to="navPath('/contact')"
          class="text-body-lg text-black-dark hover:text-primary transition-colors"
          active-class="text-primary"
        >
          {{ t('nav.contact') }}
        </RouterLink>

      </nav>

      <!-- Right actions -->
      <div class="hidden lg:flex items-center gap-6">
        <!-- Language switcher -->
        <div class="relative h-full flex items-center" @mouseenter="langOpen = true">
          <button
            class="flex items-center gap-1.5 text-black-dark hover:text-primary transition-colors cursor-pointer"
            :class="langOpen ? 'text-primary' : ''"
            aria-haspopup="listbox"
            :aria-expanded="langOpen"
          >
            <Languages :size="18" />
            <ChevronDown :size="13" class="transition-transform duration-200" :class="langOpen ? 'rotate-180' : ''" />
          </button>
          <div
            v-if="langOpen"
            class="absolute right-0 top-full mt-0 pt-0.5 z-50"
          >
            <div
              role="listbox"
              class="bg-white rounded-md shadow-dropdown flex flex-col gap-1 p-2 w-36"
            >
              <button
                v-for="opt in [{ value: 'en', label: 'English' }, { value: 'ja', label: '日本語' }]"
                :key="opt.value"
                role="option"
                :aria-selected="isActiveLocale(opt.value)"
                class="w-full flex items-center justify-between rounded-md px-4 py-2.5 text-sm text-left hover:bg-bg-light transition-colors"
                :class="isActiveLocale(opt.value) ? 'text-primary font-medium' : 'text-black-dark'"
                @click="selectLocale(opt.value as 'en' | 'ja')"
              >
                {{ opt.label }}
                <span v-if="isActiveLocale(opt.value)" class="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
              </button>
            </div>
          </div>
        </div>

        <!-- Request Demo -->
        <RouterLink :to="navPath('/request-demo')" @click="closeMenus">
          <AppButton size="sm" variant="solid">{{ t('nav.requestDemo') }}</AppButton>
        </RouterLink>
      </div>

      <!-- Mobile hamburger -->
      <button
        class="lg:hidden p-2 text-black-dark cursor-pointer"
        @click="mobileOpen = !mobileOpen"
        :aria-label="mobileOpen ? t('nav.closeMenu') : t('nav.openMenu')"
      >
        <X v-if="mobileOpen" :size="24" />
        <Menu v-else :size="24" />
      </button>
    </div>

    <!-- Mobile nav drawer -->
    <div
      v-if="mobileOpen"
      class="lg:hidden border-t border-border bg-white page-container pb-6 flex flex-col gap-2"
    >
      <RouterLink :to="navPath('')" class="py-3 text-black-dark font-medium border-b border-border" exact-active-class="text-primary" @click="closeMobile">{{ t('nav.home') }}</RouterLink>

      <!-- Products accordion -->
      <div class="border-b border-border">
        <button class="w-full flex items-center justify-between py-3 font-medium cursor-pointer" :class="isProductsActive ? 'text-primary' : 'text-black-dark'" @click="toggleMobile('products')">
          {{ t('nav.products') }}
          <ChevronDown :size="16" :class="mobileExpanded === 'products' ? 'rotate-180' : ''" class="transition-transform" />
        </button>
        <div v-if="mobileExpanded === 'products'" class="flex flex-col gap-3 pb-3 pl-4">
          <div v-for="item in productMenu" :key="item.path" class="flex flex-col gap-1">
            <p class="text-black-dark font-medium text-sm">{{ item.name }}</p>
            <RouterLink :to="navPath(item.path)" class="py-1 pl-3 text-primary text-sm" @click="closeMobile">{{ t('header.explorePlatform') }}</RouterLink>
            <RouterLink v-if="item.successPath" :to="navPath(item.successPath)" class="py-1 pl-3 text-primary text-sm" @click="closeMobile">{{ t('header.exploreSuccessStories') }}</RouterLink>
          </div>
        </div>
      </div>

      <!-- Company accordion -->
      <div class="border-b border-border">
        <button class="w-full flex items-center justify-between py-3 font-medium cursor-pointer" :class="isCompanyActive ? 'text-primary' : 'text-black-dark'" @click="toggleMobile('company')">
          {{ t('nav.company') }}
          <ChevronDown :size="16" :class="mobileExpanded === 'company' ? 'rotate-180' : ''" class="transition-transform" />
        </button>
        <div v-if="mobileExpanded === 'company'" class="flex flex-col gap-1 pb-3 pl-4">
          <RouterLink v-for="item in companyMenu" :key="item.path" :to="navPath(item.path)" class="py-1.5 text-primary text-sm" @click="closeMobile">{{ item.label }}</RouterLink>
        </div>
      </div>

      <!-- Resources accordion -->
      <div class="border-b border-border">
        <button class="w-full flex items-center justify-between py-3 font-medium cursor-pointer" :class="isResourcesActive ? 'text-primary' : 'text-black-dark'" @click="toggleMobile('resources')">
          {{ t('nav.resources') }}
          <ChevronDown :size="16" :class="mobileExpanded === 'resources' ? 'rotate-180' : ''" class="transition-transform" />
        </button>
        <div v-if="mobileExpanded === 'resources'" class="flex flex-col gap-1 pb-3 pl-4">
          <template v-for="item in resourceMenu" :key="item.label">
            <a v-if="item.external" :href="item.href" target="_blank" rel="noopener noreferrer" class="flex items-center gap-1 py-1.5 text-primary text-sm" @click="closeMobile">{{ item.label }} <ExternalLink :size="13" /></a>
            <RouterLink v-else :to="navPath(item.path)" class="py-1.5 text-primary text-sm" @click="closeMobile">{{ item.label }}</RouterLink>
          </template>
        </div>
      </div>

      <RouterLink :to="navPath('/contact')" class="py-3 text-black-dark font-medium border-b border-border" @click="closeMobile">{{ t('nav.contact') }}</RouterLink>

      <div class="flex items-center pt-2">
        <div class="flex items-center gap-2">
          <Languages :size="16" class="text-black-dark" />
          <button
            v-for="opt in [{ value: 'en', label: 'EN' }, { value: 'ja', label: 'JA' }]"
            :key="opt.value"
            class="text-sm px-2 py-0.5 rounded transition-colors"
            :class="isActiveLocale(opt.value) ? 'text-primary font-medium bg-primary/10' : 'text-black-dark'"
            @click="selectLocale(opt.value as 'en' | 'ja')"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>

      <RouterLink :to="navPath('/request-demo')" class="mt-4" @click="closeMobile">
        <AppButton size="md" variant="solid" class="w-full">{{ t('nav.requestDemo') }}</AppButton>
      </RouterLink>
    </div>
  </header>

  <!-- Spacer to offset fixed header -->
  <div class="h-20" aria-hidden="true" />
</template>
